/**
 * Cross-launch persistence for the measured iOS keyboard height, keyed by the
 * layout-viewport height (`window.innerHeight`).
 *
 * Why (founder device report, 2026-08-21): `inlineKeyboardGuard`'s reveal is
 * only as good as its keyboard-height prediction, and that prediction used to
 * reset to a hardcoded guess on EVERY app launch — so the FIRST focus of each
 * session under-revealed by (real − guessed)/2 px and iOS panned the
 * difference: a small residual lurch, worst in the installed PWA whose
 * keyboard is taller than Safari's (Safari's own bottom bar overlaps part of
 * the band).
 *
 * Why keyed by innerHeight (total-coverage pass, same day): the keyboard's
 * height differs per ORIENTATION and per window shape (portrait vs landscape
 * vs iPad split view), and innerHeight is stable across a keyboard open on
 * iOS — so it identifies the viewport configuration exactly. A single
 * unkeyed value would cross-pollute orientations and re-introduce the
 * under-prediction after every rotation.
 *
 * Storage is per-container on iOS (Safari and the installed PWA do NOT share
 * localStorage), which is correct here: their keyboard geometries differ, so
 * each container keeps its own truth.
 */

const STORAGE_KEY = 'cvx:kb-heights';

/** Sanity bounds: a keyboard outside these is a mis-measurement, not a keyboard. */
const MIN_PLAUSIBLE_PX = 120;
const MAX_PLAUSIBLE_PX = 600;

function readMap() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Keys are `"<innerHeight>:<kind>"` (kind = 'numeric' | 'text'). Exact-kind
 * only: a legacy `"<innerHeight>"` entry (pre-kind, unknown kind) is NEVER
 * returned here — see `loadLegacyKeyboardHeight`.
 *
 * @param {number} innerHeight the layout-viewport height the measurement belongs to
 * @param {string} [kind]
 * @returns {number|null} the persisted height for that viewport + kind, or null.
 */
export function loadPersistedKeyboardHeight(innerHeight, kind = 'text') {
  if (typeof window === 'undefined') return null;
  const map = readMap();
  const base = String(Math.round(innerHeight));
  const v = parseFloat(map[`${base}:${kind}`]);
  return Number.isFinite(v) && v >= MIN_PLAUSIBLE_PX && v <= MAX_PLAUSIBLE_PX ? v : null;
}

/**
 * The pre-kind `"<innerHeight>"` entry, if plausible. Its kind is unknown, so
 * it may only feed a "tallest known" fallback, never a specific kind's value.
 */
export function loadLegacyKeyboardHeight(innerHeight) {
  if (typeof window === 'undefined') return null;
  const v = parseFloat(readMap()[String(Math.round(innerHeight))]);
  return Number.isFinite(v) && v >= MIN_PLAUSIBLE_PX && v <= MAX_PLAUSIBLE_PX ? v : null;
}

/** Best-effort persist; storage errors (private mode, quota) never propagate. */
export function persistKeyboardHeight(innerHeight, h, kind = 'text') {
  if (typeof window === 'undefined') return;
  if (!Number.isFinite(innerHeight) || innerHeight <= 0) return;
  if (!Number.isFinite(h) || h < MIN_PLAUSIBLE_PX || h > MAX_PLAUSIBLE_PX) return;
  try {
    const map = readMap();
    map[`${Math.round(innerHeight)}:${kind}`] = Math.round(h);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* best-effort */
  }
}
